import PropTypes from 'prop-types';
import React, { useState, useCallback } from 'react';

import { getContactAPI } from '@sophocles/api/contacts';

import AddressBookDialog from '../AddressBookDialog/AddressBookDialog';
import AddressDisplayCard from '../AddressDisplayCard/AddressDisplayCard';
import ContactForm from '../ContactForm';

/**
 * ContactSelector - A unified component for contact selection and editing
 *
 * This component encapsulates all contact selection logic:
 * - Displays the currently selected contact via AddressDisplayCard
 * - Opens AddressBookDialog when "Change" is clicked
 * - Opens ContactForm directly when "Edit" is clicked (standalone mode)
 * - Handles the _isAddress/_isManualEntry flags for proper useOnceOnly behavior
 * - Fetches full contact data when editing saved contacts
 *
 * Usage:
 * ```jsx
 * <ContactSelector
 *   value={selectedShippingContact}
 *   onChange={setSelectedShippingContact}
 *   customerId={customerId}
 *   siteGroupId={siteGroupId}
 *   customer={customer}
 *   siteGroup={siteGroup}
 *   addressType="shipping"
 *   label="Shipping Contact"
 *   required
 * />
 * ```
 *
 * The parent component only needs to manage:
 * - value: the currently selected contact object
 * - onChange: callback when contact is selected/saved
 *
 * All dialog state, edit handlers, flag logic, and contact fetching
 * is handled internally by this component.
 */
const ContactSelector = ({
  // Core props
  value,
  onChange,

  // Context props
  customerId,
  siteGroupId,
  customer,
  siteGroup,
  customerName: customerNameProp,
  siteGroupName: siteGroupNameProp,

  // Display props
  label,
  addressType = 'shipping',
  required = false,
  disabled = false,
  showAddress = true,
  error = false,
  helperText,

  // Validation props
  validationRules,
  onValidationChange,

  // Behavior props
  requiredFields = ['address', 'name'],
  primaryContext = 'rma',
  showDefaultAddresses = true,
  quickMode = false,
  requireCompleteAddress = true,

  // Style props
  sx,
}) => {
  // Internal dialog state
  const [addressBookOpen, setAddressBookOpen] = useState(false);
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const [contactFormData, setContactFormData] = useState(null);
  const [editingContact, setEditingContact] = useState(null);

  // Derive customer/siteGroup names for ContactForm - prefer explicit props over object lookups
  const customerName =
    customerNameProp || (customer?.get ? customer.get('name', 'Customer') : customer?.name || 'Customer');
  const siteGroupName =
    siteGroupNameProp || (siteGroup?.get ? siteGroup.get('name', 'Site Group') : siteGroup?.name || 'Site Group');

  /**
   * Handle contact selection from AddressBookDialog
   * Keeps _isAddress and _isManualEntry flags for proper edit behavior
   */
  const handleSelect = useCallback(
    contact => {
      if (!contact) return;
      onChange(contact);
      setAddressBookOpen(false);
      setEditingContact(null);
    },
    [onChange],
  );

  /**
   * Handle edit click - opens ContactForm directly (standalone mode)
   * Fetches full contact data if it's a saved contact with ID
   */
  const handleEdit = useCallback(async contact => {
    let contactToEdit = contact;

    // If contact has an ID and isn't an address, try to fetch full contact data
    if (contact?.id && !contact._isAddress && !contact._isManualEntry) {
      try {
        const response = await getContactAPI(contact.id);
        if (response?.data) {
          contactToEdit = response.data;
        }
      } catch (e) {
        // Use the contact data we have if fetch fails
      }
    }

    setContactFormData(contactToEdit);
    setContactFormOpen(true);
  }, []);

  /**
   * Handle contact form success - passes contact to parent and closes form
   */
  const handleContactFormSuccess = useCallback(
    contact => {
      onChange(contact);
      setContactFormOpen(false);
      setContactFormData(null);
    },
    [onChange],
  );

  /**
   * Handle contact form close
   */
  const handleContactFormClose = useCallback(() => {
    setContactFormOpen(false);
    setContactFormData(null);
  }, []);

  /**
   * Handle "Back to Address Book" from ContactForm
   */
  const handleBackToAddressBook = useCallback(() => {
    setContactFormOpen(false);
    setContactFormData(null);
    setAddressBookOpen(true);
  }, []);

  /**
   * Handle edit from within AddressBookDialog
   */
  const handleEditFromDialog = useCallback((contact, type) => {
    setEditingContact(contact);
  }, []);

  /**
   * Handle "Add New" from AddressBookDialog
   */
  const handleAddNew = useCallback(() => {
    setAddressBookOpen(false);
  }, []);

  // Note: hasExistingContact and useOnceOnly are now derived internally by ContactForm
  // based on the contact prop, so we no longer need to calculate and pass them

  return (
    <>
      {/* Display Card */}
      <AddressDisplayCard
        title={label}
        contact={value}
        onChangeClick={() => setAddressBookOpen(true)}
        onEdit={handleEdit}
        required={required}
        disabled={disabled}
        showAddress={showAddress}
        error={error}
        helperText={helperText}
        validationRules={validationRules}
        onValidationChange={onValidationChange}
        sx={sx}
      />

      {/* Address Book Dialog */}
      <AddressBookDialog
        open={addressBookOpen}
        onClose={() => {
          setAddressBookOpen(false);
          setEditingContact(null);
        }}
        onSelect={handleSelect}
        customerId={customerId}
        siteGroupId={siteGroupId}
        customer={customer}
        siteGroup={siteGroup}
        value={value}
        onEdit={handleEditFromDialog}
        onAddNew={handleAddNew}
        showDefaultAddresses={showDefaultAddresses}
        initialEditingContact={editingContact}
        primaryContext={primaryContext}
        requiredFields={requiredFields}
        addressType={addressType}
        quickMode={quickMode}
        requireCompleteAddress={requireCompleteAddress}
      />

      {/* Standalone Contact Form - hasExistingContact/useOnceOnly now derived from contact prop */}
      <ContactForm
        open={contactFormOpen}
        onClose={handleContactFormClose}
        contact={contactFormData}
        customerId={customerId}
        siteGroupId={siteGroupId}
        onSuccess={handleContactFormSuccess}
        standaloneMode={true}
        showBackButton={true}
        onBackToAddressBook={handleBackToAddressBook}
        customerName={customerName}
        siteGroupName={siteGroupName}
        primaryContext={primaryContext}
        requiredFields={requiredFields}
      />
    </>
  );
};

ContactSelector.propTypes = {
  /** Currently selected contact object */
  value: PropTypes.object,
  /** Callback when contact is selected or saved - receives the contact object */
  onChange: PropTypes.func.isRequired,

  /** Customer ID for contact filtering */
  customerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Site Group ID for contact filtering */
  siteGroupId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Customer object (Immutable Map or plain object) for display */
  customer: PropTypes.object,
  /** Site Group object (Immutable Map or plain object) for display */
  siteGroup: PropTypes.object,
  /** Customer name string (alternative to customer object) */
  customerName: PropTypes.string,
  /** Site Group name string (alternative to siteGroup object) */
  siteGroupName: PropTypes.string,

  /** Label for the display card (e.g., "Shipping Contact", "Billing Address") */
  label: PropTypes.string,
  /** Type of address: 'shipping' or 'billing' */
  addressType: PropTypes.oneOf(['shipping', 'billing']),
  /** Whether a contact selection is required */
  required: PropTypes.bool,
  /** Whether the selector is disabled */
  disabled: PropTypes.bool,
  /** Whether to show the full address in the display card */
  showAddress: PropTypes.bool,
  /** Whether to show error state on the display card */
  error: PropTypes.bool,
  /** Helper text to display below the display card */
  helperText: PropTypes.string,

  /** Validation rules for AddressDisplayCard (e.g., { requireName: true, requireAddress: true }) */
  validationRules: PropTypes.shape({
    requireName: PropTypes.bool,
    requireAddress: PropTypes.bool,
    requireEmailOrPhone: PropTypes.bool,
  }),
  /** Callback when validation state changes - receives boolean isValid */
  onValidationChange: PropTypes.func,

  /** Array of required fields for validation */
  requiredFields: PropTypes.arrayOf(PropTypes.string),
  /** Context for primary options: 'all', 'rma', 'sales', or 'none' */
  primaryContext: PropTypes.oneOf(['all', 'rma', 'sales', 'none']),
  /** Whether to show default addresses in the address book */
  showDefaultAddresses: PropTypes.bool,
  /** Whether to use quick mode (minimal fields) */
  quickMode: PropTypes.bool,
  /** Whether to require complete address */
  requireCompleteAddress: PropTypes.bool,

  /** Custom styles */
  sx: PropTypes.object,
};

export default ContactSelector;
